import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentLanguage, setLanguage as setStoredLanguage, isRTL } from '../utils/i18n/config';
import { getTranslation } from '../utils/i18n/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    // Set initial document direction and language attribute
    const langDetails = isRTL(currentLanguage);
    document.documentElement.dir = langDetails ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;

    // Listen for language changes from other components
    const handleLanguageChange = (e) => {
      if (e?.detail?.language && e?.detail?.language !== currentLanguage) {
        setCurrentLanguage(e?.detail?.language);
      }
    };

    const handleStorageChange = () => {
      const newLang = getCurrentLanguage();
      if (newLang !== currentLanguage) {
        setCurrentLanguage(newLang);
      }
    };

    window.addEventListener('languagechange', handleLanguageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('languagechange', handleLanguageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentLanguage]);

  const changeLanguage = (langCode) => {
    if (setStoredLanguage(langCode)) {
      setCurrentLanguage(langCode);
      return true;
    }
    return false;
  };

  const t = (keyPath) => getTranslation(keyPath, currentLanguage);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    isRTL: isRTL(currentLanguage)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
function useAuth(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: useAuth is not implemented yet.', args);
  return null;
}

export { useAuth };